import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import { Filter } from "lucide-react";

function fmtTime(s) { const m = Math.floor(s / 60), sec = s % 60; return `${m}m ${sec}s`; }
function fmtDate(iso) { try { return new Date(iso).toLocaleString(); } catch { return iso; } }

export default function AdminResults() {
  const [attempts, setAttempts] = useState([]);
  const [students, setStudents] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ student_id: "", exercise_id: "", level: "" });

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.student_id) params.student_id = filters.student_id;
      if (filters.exercise_id) params.exercise_id = filters.exercise_id;
      if (filters.level) params.level = filters.level;
      const [a, s, e] = await Promise.all([
        api.get("/admin/attempts", { params }),
        api.get("/admin/students"),
        api.get("/admin/exercises"),
      ]);
      setAttempts(a.data || []);
      setStudents(s.data || []);
      setExercises(e.data || []);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters.student_id, filters.exercise_id, filters.level]);

  const levels = useMemo(() => [...new Set(exercises.map((e) => e.level))].sort(), [exercises]);

  return (
    <AppLayout variant="admin">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-ink mb-2">Results</h1>
        <p className="text-stone mb-6">All completed exercise attempts across the platform.</p>

        <div className="p-4 bg-white rounded-3xl card-tactile mb-6 flex flex-wrap gap-3 items-end">
          <div className="flex items-center gap-2 text-stone font-bold text-sm"><Filter className="w-4 h-4" /> Filters</div>
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-bold text-stone uppercase mb-1">Student</label>
            <select data-testid="filter-student" value={filters.student_id} onChange={(e) => setFilters((f) => ({ ...f, student_id: e.target.value }))} className="w-full h-11 px-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none">
              <option value="">All students</option>
              {students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="block text-xs font-bold text-stone uppercase mb-1">Exercise</label>
            <select data-testid="filter-exercise" value={filters.exercise_id} onChange={(e) => setFilters((f) => ({ ...f, exercise_id: e.target.value }))} className="w-full h-11 px-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none">
              <option value="">All exercises</option>
              {exercises.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
            </select>
          </div>
          <div className="min-w-[120px]">
            <label className="block text-xs font-bold text-stone uppercase mb-1">Level</label>
            <select data-testid="filter-level" value={filters.level} onChange={(e) => setFilters((f) => ({ ...f, level: e.target.value }))} className="w-full h-11 px-3 rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none">
              <option value="">All</option>
              {levels.map((lv) => <option key={lv} value={lv}>Level {lv}</option>)}
            </select>
          </div>
          <button data-testid="clear-filters-button" onClick={() => setFilters({ student_id: "", exercise_id: "", level: "" })} className="h-11 px-4 rounded-full bg-white border-2 border-cloud font-bold text-ink btn-tactile">Clear</button>
        </div>

        <div className="bg-white rounded-3xl card-tactile overflow-hidden">
          {loading ? (
            <div className="p-8 text-stone">Loading…</div>
          ) : attempts.length === 0 ? (
            <div className="p-8 text-center text-stone">No results match the current filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-canvas">
                  <tr className="text-left text-xs uppercase tracking-wider text-stone">
                    <th className="p-4 font-bold">Student</th>
                    <th className="p-4 font-bold">Exercise</th>
                    <th className="p-4 font-bold">Level</th>
                    <th className="p-4 font-bold">Date</th>
                    <th className="p-4 font-bold">Score</th>
                    <th className="p-4 font-bold">Accuracy</th>
                    <th className="p-4 font-bold">Time</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody data-testid="results-table" className="divide-y divide-cloud">
                  {attempts.map((a) => (
                    <tr key={a.id} className="hover:bg-canvas/50">
                      <td className="p-4 font-semibold text-ink">{a.student_name}</td>
                      <td className="p-4 text-stone">{a.exercise_title}</td>
                      <td className="p-4"><span className="px-2 py-1 rounded-full bg-sky-light text-sky-hover text-xs font-bold">L{a.level ?? "-"}</span></td>
                      <td className="p-4 text-stone">{fmtDate(a.submitted_at)}</td>
                      <td className="p-4 font-heading font-bold">{a.score}/{a.total_questions}</td>
                      <td className="p-4 font-semibold text-leaf">{a.accuracy}%</td>
                      <td className="p-4 text-stone">{fmtTime(a.time_taken_seconds)}</td>
                      <td className="p-4"><Link data-testid={`view-attempt-${a.id}`} to={`/admin/attempts/${a.id}`} className="text-sky font-bold hover:underline">View</Link></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
